import { MapPinned, TriangleAlert } from 'lucide-react';
import type { Incident, NearbyNote } from '@duty/shared';
import { localKnowledgeFor, attributionFor } from '@/incident/spatial';

const STALE_DAYS = 365;

function ageLabel(capturedAt?: string): { text: string; stale: boolean } {
  if (!capturedAt) return { text: 'undated', stale: true };
  const days = Math.floor((Date.now() - Date.parse(capturedAt)) / 86_400_000);
  const stale = days > STALE_DAYS;
  if (days < 1) return { text: 'today', stale };
  if (days < 60) return { text: `${days}d ago`, stale };
  const months = Math.floor(days / 30);
  if (months < 24) return { text: `${months}mo ago`, stale };
  return { text: `${Math.floor(days / 365)}y ago`, stale };
}

/** Seeded local knowledge near the incident, with provenance + staleness. */
export function LocalKnowledgePanel({ incident }: { incident: Incident }) {
  const notes = localKnowledgeFor(incident);
  const attribution = attributionFor(incident);

  if (!incident.coordinates) {
    return <p className="text-[11px] italic text-muted">No locality data for this incident.</p>;
  }

  return (
    <div className="space-y-2">
      {notes.length === 0 ? (
        <p className="text-[11px] italic text-muted">No local knowledge within range.</p>
      ) : (
        <ul className="space-y-1.5">
          {notes.map((n) => (
            <NoteRow key={n.label} note={n} />
          ))}
        </ul>
      )}
      {attribution.length > 0 && (
        <p className="border-t border-border pt-2 text-[10px] leading-snug text-muted">
          {attribution.join(' ')}
        </p>
      )}
    </div>
  );
}

function NoteRow({ note }: { note: NearbyNote }) {
  const age = ageLabel(note.capturedAt);
  return (
    <li className="rounded-md border border-border bg-surface-2/60 p-2 text-xs">
      <div className="flex items-start gap-1.5">
        <MapPinned size={13} className="mt-0.5 shrink-0 text-accent" />
        <span className="text-text/90">{note.label}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10px] text-muted">
        <span className="font-mono">~{note.distanceM} m</span>
        <span className="chip !px-1.5 !py-0">{note.provenance}</span>
        <span className={`inline-flex items-center gap-0.5 ${age.stale ? 'text-high' : ''}`}>
          {age.stale && <TriangleAlert size={10} />}
          {age.text}
          {age.stale && ' · verify'}
        </span>
      </div>
    </li>
  );
}
